import React, { useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';

import Swal from 'sweetalert2';
import Button from 'react-bootstrap/Button';

import { selectStatusByName, selectSectionAddClicksByName } from '@redux/selectors/FormSelector';
import { selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';
import UnitCard from './UnitCard';

const MAX = 3;
const whiteList = [
  'UnitTypeType',
  'UnitTypeTotalRent',
  'UnitTypeDescription',
  'UnitTypeActualRent',
  'UnitTypeUnitsTotal',
];
const SortableCard = SortableElement(props => (
  <UnitCard {...props} />
));

const Grid = SortableContainer(({
  fileList,
  fieldsList,
  validateFigField,
  something,
  ...otherProps
}) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          validateFigField={validateFigField}
          fieldsList={fieldsList}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const RepeatableUnits = ({ fields, validateFigField, unitsPayload }) => {
  const {
    values, setValues, setFieldValue,
  } = useFormikContext();

  const statusUnits = useSelector(state => selectStatusByName(state, 'Units'));
  const addClickCounter = useSelector(state => selectSectionAddClicksByName(state, 'Units'));
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const unitsCount = sectionsPerRepeatableSection.Units.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.Units) {
      Object.entries(values.Units).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.Units]);

  const fieldsList = useMemo(() => {
    const array = [];
    fields.forEach(field => {
      if (whiteList.includes(field.FieldName)) {
        array.push(field);
      }
    });
    return array;
  }, [fields]);

  const buildFormikValues = () => {
    const newValues = {};
    let index = 0;
    unitsPayload.sort((a, b) => a.SortOrder - b.SortOrder).forEach(entry => {
      const uid = uuidv4(); // keep this an unique string that cannot be converted to a number (prevents keys reordering)
      newValues[uid] = {
        uid,
        UniqueID: entry.UniqueID,
        UnitTypeType: entry.UnitTypeType,
        UnitTypeTotalRent: entry.UnitTypeTotalRent,
        UnitTypeDescription: entry.UnitTypeDescription,
        UnitTypeActualRent: entry.UnitTypeActualRent,
        UnitTypeUnitsTotal: entry.UnitTypeUnitsTotal,
        SortOrder: entry.SortOrder,
        IsDeleted: entry.IsDeleted,
        IsLive: entry.IsLive,
      };
      // ensure correct sort order
      if (!entry.IsDeleted) {
        newValues[uid].SortOrder = index + 1;
        index += 1;
      }
    });
    return newValues;
  };

  useEffect(() => {
    if (unitsPayload?.length) {
      const newValues = buildFormikValues();
      setFieldValue('Units', {
        ...values.Units,
        ...newValues,
      });
    }
  }, [unitsPayload]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Units Exceeded',
    html: `The maximum number of units must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  const addUnit = () => {
    if (unitsCount + 1 > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    const uid = uuidv4();
    setValues({
      ...values,
      Units: {
        ...values.Units,
        [uid]: {
          uid,
          UnitTypeType: '',
          UnitTypeTotalRent: '',
          UnitTypeDescription: '',
          UnitTypeActualRent: '',
          UnitTypeUnitsTotal: '',
          SortOrder: fileList.length + 1, // fileList excludes deleted entries
          IsDeleted: false,
          IsLive: false,
        },
      },
    });
  };

  // after sort ends derive new order, then set formik state
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    // preserve deleted entries - excluded from fileList
    Object.entries(values.Units).forEach(file => {
      if (file[1].IsDeleted) {
        newValues = {
          ...newValues,
          [file[0]]: {
            ...file[1],
          },
        };
      }
    });
    setValues({
      ...values,
      Units: { ...newValues },
    });
  };

  useEffect(() => {
    if (addClickCounter) {
      addUnit();
    }
  }, [addClickCounter]);

  return (
    <>
      { statusUnits?.errorMessage && <div className="invalid-feedback">{statusUnits.errorMessage}</div> }
      <Button
        name="btnAddUnit"
        className="float-right"
        onClick={addUnit}
        size="sm"
      >
        <i className="fas fa-plus mr-2" />
        Add Unit
      </Button>
      <br className="clearfix" />
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        fieldsList={fieldsList}
        validateFigField={validateFigField}
        onSortEnd={onSortEnd}
        useDragHandle
      />
      <br className="clearfix" />
      {(unitsCount > MAX) && (
        <>
          <Button
            name="btnAddUnit"
            className="float-right"
            onClick={addUnit}
            size="sm"
          >
            <i className="fas fa-plus mr-2" />
            Add Unit
          </Button>
          <br className="clearfix" />
        </>
      )}
    </>
  );
};

RepeatableUnits.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  validateFigField: PropTypes.func,
  unitsPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default RepeatableUnits;
